import { useEventListener } from 'hooks/useEventListener';
import { useWindowSize } from 'hooks/useWindowSize';
import {
  selectEditBackgroundView,
  selectIsAccessibilityView,
  selectIsSlideView,
  selectIsWidgetSettingsView,
  selectZoom,
} from 'modules/Editor/store/editorSettingsSelector';
import { useAppSelector } from 'modules/Editor/store/hooks';
import { selectInfographSize } from 'modules/Editor/store/infographSelector';
import { memo, useEffect } from 'react';

import { SmartGuideConfig } from './BoundingBox.types';

type UpdateBoundingRectProps = {
  setSmartGuideConfig: (config: SmartGuideConfig) => void;
  moveableRef: React.MutableRefObject<any>;
};
// Updates the bounding box rect position
export const UpdateBoundingRect = memo(({ setSmartGuideConfig, moveableRef }: UpdateBoundingRectProps) => {
  const windowSize = useWindowSize();
  const infoGraphSize = useAppSelector(selectInfographSize);
  const hasAccessibilityView = useAppSelector(selectIsAccessibilityView);
  const hasWidgetSettingsView = useAppSelector(selectIsWidgetSettingsView);
  const isSlideView = useAppSelector(selectIsSlideView);
  const isEditBackgroundView = useAppSelector(selectEditBackgroundView);
  const zoom = useAppSelector(selectZoom);

  // Update the bounding box rect position - in response to the dependency changes
  useEffect(() => {
    if (!moveableRef || !moveableRef.current) return;

    moveableRef.current.updateRect();
    setSmartGuideConfig({ zoomPercent: zoom * 100 });
  }, [
    zoom,
    windowSize,
    setSmartGuideConfig,
    hasAccessibilityView,
    hasWidgetSettingsView,
    infoGraphSize,
    isSlideView,
    isEditBackgroundView,
    moveableRef,
  ]);

  // Selection is kept after undo/redo - and if a widget changes position/size the bounding box rect is not updated
  useEventListener('history', () => {
    setTimeout(() => moveableRef.current?.updateRect(), 0);
  });

  return null;
});

UpdateBoundingRect.displayName = 'UpdateBoundingRect';
